import React from 'react';
import toast from 'react-hot-toast';
import { Radio, WarningCircle, X } from '@phosphor-icons/react';

type AtcToastTone = 'warning' | 'danger';

interface AtcToastCardProps {
  toastId: string;
  tone: AtcToastTone;
  eyebrow: string;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ShowAtcToastOptions {
  id: string;
  tone: AtcToastTone;
  eyebrow: string;
  title: string;
  message: string;
  duration?: number;
  actionLabel?: string;
  onAction?: () => void;
}

const toneStyles: Record<AtcToastTone, {
  glow: string;
  iconBorder: string;
  iconBackground: string;
  iconColor: string;
  Icon: typeof WarningCircle;
}> = {
  warning: {
    glow: 'from-amber-300/30 via-sky-300/16 to-transparent',
    iconBorder: 'border-amber-200/35',
    iconBackground: 'bg-amber-200/12',
    iconColor: 'text-amber-100',
    Icon: Radio,
  },
  danger: {
    glow: 'from-rose-300/34 via-orange-300/14 to-transparent',
    iconBorder: 'border-rose-200/35',
    iconBackground: 'bg-rose-200/12',
    iconColor: 'text-rose-100',
    Icon: WarningCircle,
  },
};

const renderAtcToastCard = ({
  toastId,
  tone,
  eyebrow,
  title,
  message,
  actionLabel,
  onAction,
}: AtcToastCardProps) => {
  const styles = toneStyles[tone];
  const Icon = styles.Icon;

  return (
    <div className="pointer-events-auto relative overflow-hidden rounded-[22px] border border-white/12 bg-slate-950/88 shadow-[0_24px_90px_rgba(15,23,42,0.55)] backdrop-blur-2xl">
      <div className={`absolute inset-0 bg-gradient-to-br ${styles.glow}`} />
      <div className="absolute inset-px rounded-[21px] bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_46%)]" />

      <div className="relative flex max-w-[26rem] items-start gap-3 px-4 py-4 text-slate-100">
        <div
          className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${styles.iconBorder} ${styles.iconBackground} ${styles.iconColor}`}
        >
          <Icon size={21} weight="fill" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[0.64rem] font-semibold uppercase tracking-[0.28em] text-sky-100/60">
            {eyebrow}
          </p>
          <p className="mt-1 text-sm font-semibold leading-5 text-white">{title}</p>
          <p className="mt-1 text-sm leading-5 text-slate-200/90">{message}</p>

          {actionLabel && onAction ? (
            <button
              type="button"
              className="mt-3 inline-flex items-center rounded-xl border border-white/14 bg-white/8 px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/55"
              onClick={() => {
                toast.dismiss(toastId);
                onAction();
              }}
            >
              {actionLabel}
            </button>
          ) : null}
        </div>

        <button
          type="button"
          className="shrink-0 rounded-full border border-white/10 bg-white/6 p-2 text-slate-300 transition hover:bg-white/12 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/55"
          aria-label="Dismiss notification"
          onClick={() => toast.dismiss(toastId)}
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
};

const showAtcToast = ({
  id,
  tone,
  eyebrow,
  title,
  message,
  duration = 8000,
  actionLabel,
  onAction,
}: ShowAtcToastOptions) => {
  const messageText = `${title}. ${message}`;

  if (typeof toast.custom !== 'function') {
    return toast.error(messageText, { id, duration });
  }

  return toast.custom(
    (toastInstance) =>
      renderAtcToastCard({
        toastId: toastInstance.id,
        tone,
        eyebrow,
        title,
        message,
        actionLabel,
        onAction,
      }),
    {
      id,
      duration,
    }
  );
};

export const showAtcStationFallbackToast = ({
  unavailableStationName,
  fallbackStationName,
  toastId,
}: {
  unavailableStationName: string;
  fallbackStationName: string;
  toastId: string;
}) =>
  showAtcToast({
    id: toastId,
    tone: 'warning',
    eyebrow: 'ATC station issue',
    title: 'Station unavailable',
    message: `${unavailableStationName} is not available right now. Switched you to ${fallbackStationName}.`,
    duration: 9000,
  });

export const showAtcStationUnavailableToast = ({
  stationName,
  message,
  toastId,
}: {
  stationName: string;
  message: string;
  toastId: string;
}) =>
  showAtcToast({
    id: toastId,
    tone: 'warning',
    eyebrow: 'ATC station issue',
    title: 'Connection problem',
    message: message || `${stationName} could not be reached right now.`,
    duration: 9000,
  });

export const showAtcBrokenLinksToast = ({
  stationName,
  toastId,
  onRefresh,
}: {
  stationName?: string;
  toastId: string;
  onRefresh: () => void;
}) =>
  showAtcToast({
    id: toastId,
    tone: 'danger',
    eyebrow: 'ATC playback issue',
    title: 'Scenarios unavailable',
    message: stationName
      ? `${stationName} returned stale or broken audio links. Refresh the page to reconnect.`
      : 'This station returned stale or broken audio links. Refresh the page to reconnect.',
    duration: 12000,
    actionLabel: 'Refresh page',
    onAction: onRefresh,
  });